/**
 * Folha de estilo do domo, injetada por JS em vez de um `Dome.css` importado.
 *
 * PORQUÊ: `import './Dome.css'` só compila se existir um `declare module
 * '*.css'` no projeto — e esse arquivo teria de morar fora deste diretório,
 * que não é meu para editar. Injetar de dentro do módulo mantém o domo
 * autocontido, sem tocar em tsconfig, vite.config nem em um vite-env.d.ts
 * compartilhado. A regra é: aqui só entra MOVIMENTO e geometria fixa; cor
 * nenhuma — cor vem de DOME[state].color, sempre por style inline.
 *
 * As curvas e durações são as mesmas do app legado (os `@keyframes wd-*`).
 * Duas adaptações foram necessárias ao migrar de <div> para SVG:
 *  - `transform-box: fill-box` em tudo que gira ou escala, senão a origem do
 *    transform seria a do viewBox e o olho piscaria em torno do canto.
 *  - o que o legado animava por `margin-top` (sobrancelha inquieta) virou
 *    translateY, porque margem não existe em SVG.
 */

const CSS = `
.dome-root {
  position: relative;
  display: block;
  width: 100%;
  aspect-ratio: 1;
  --dome-level: 0;
  /* amplitude e ritmo da respiração; cada estado ajusta os dois por inline */
  --dome-respiro-amp: 1.012;
  --dome-respiro-dur: 5.2s;
}
.dome-aura {
  position: absolute; inset: -7%;
  border-radius: 50%;
  filter: blur(26px);
  pointer-events: none;
  animation: dome-halo 4.6s ease-in-out infinite;
}
.dome-ring {
  position: absolute; inset: 0;
  border-radius: 50%;
  padding: 2.6%;
  box-sizing: border-box;
  /* O domo respira sempre — é o que separa "em repouso" de "congelado". A
     amplitude é pequena de propósito: precisa ser percebida sem ser notada. */
  animation: dome-respiro var(--dome-respiro-dur) ease-in-out infinite;
  will-change: transform;
}
.dome-shell {
  width: 100%; height: 100%;
  border-radius: 50%;
  background: radial-gradient(circle at 50% 22%, #0b1119 0%, #05080d 62%, #01040a 100%);
  box-shadow: inset 0 0 60px rgba(0,0,0,.9), inset 0 2px 20px rgba(255,217,160,.09);
}
/* parafuso/LED no topo do casco — detalhe herdado do domo original */
.dome-led {
  position: absolute; top: 3.2%; left: 50%;
  transform: translateX(-50%);
  width: 2.4%; aspect-ratio: 1;
  border-radius: 50%;
  background: radial-gradient(circle at 40% 35%, #1d2c3a, #05090f 70%);
  box-shadow: inset 0 0 3px rgba(148,215,255,.5), 0 0 7px rgba(0,0,0,.8);
  z-index: 4;
}
.dome-face {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  z-index: 3;
  overflow: visible;
}
.dome-fx { transform-box: fill-box; transform-origin: center; }
.dome-fx-left { transform-box: fill-box; transform-origin: left center; }

@keyframes dome-respiro {
  0%,100% { transform: scale(1); }
  50% { transform: scale(var(--dome-respiro-amp)); }
}
@keyframes dome-breathe { 0%,100% { opacity:.6; transform: scale(.98); } 50% { opacity:1; transform: scale(1.02); } }
@keyframes dome-halo { 0%,100% { opacity:.55; } 50% { opacity:1; } }
@keyframes dome-spin { to { transform: rotate(360deg); } }
@keyframes dome-shock { 0% { transform: scale(.62); opacity:.85; } 100% { transform: scale(1.18); opacity:0; } }
@keyframes dome-gaze {
  0%, 14% { transform: translate(0,0); }
  20%, 33% { transform: translate(-11%,-5%); }
  40%, 52% { transform: translate(10%,3%); }
  59%, 68% { transform: translate(-4%,6%); }
  76%, 100% { transform: translate(0,0); }
}
@keyframes dome-ponder-gaze {
  0%, 12% { transform: translate(0,0); }
  24%, 46% { transform: translate(-9%,-13%); }
  58%, 78% { transform: translate(7%,-10%); }
  88%, 100% { transform: translate(0,0); }
}
@keyframes dome-sad-gaze {
  0%, 100% { transform: translateY(3%); }
  50% { transform: translate(-2%,4%); }
}
@keyframes dome-tilt {
  0%, 100% { transform: rotate(0deg) translateY(0); }
  30%, 70% { transform: rotate(6deg) translateY(-3px); }
}
@keyframes dome-shrug {
  0%, 100% { transform: rotate(-4deg) translateX(-3px); }
  50% { transform: rotate(4deg) translateX(3px); }
}
@keyframes dome-sweep {
  0% { transform: translateX(-46%); opacity:0; }
  30%,70% { opacity:1; }
  100% { transform: translateX(46%); opacity:0; }
}
@keyframes dome-talk { 0%,100% { transform: scaleY(.28); } 50% { transform: scaleY(1); } }
@keyframes dome-scanline { 0% { transform: translateY(0); } 100% { transform: translateY(9px); } }
@keyframes dome-rush { 0%,100% { transform: translateX(-6%); opacity:.35; } 50% { transform: translateX(6%); opacity:.9; } }
@keyframes dome-dash { 0%,100% { transform: translateX(-5%); } 50% { transform: translateX(5%); } }
@keyframes dome-fretty { 0%,100% { transform: translateY(0); opacity:.85; } 50% { transform: translateY(-4px); opacity:1; } }
@keyframes dome-glowy { 0%,100% { opacity:.78; } 50% { opacity:1; } }
@keyframes dome-boot {
  0% { transform: scaleX(0); opacity:0; }
  22% { opacity:1; }
  70% { transform: scaleX(1); opacity:1; }
  100% { transform: scaleX(1); opacity:.15; }
}
@keyframes dome-tear {
  0%, 8% { transform: translateY(-3px) scale(.4); opacity:0; }
  22% { transform: translateY(0) scale(1); opacity:.85; }
  58% { transform: translateY(14px) scale(.95); opacity:.7; }
  78%, 100% { transform: translateY(24px) scale(.7); opacity:0; }
}
/* sono REM: a sobrancelha treme sem que o rosto acorde */
@keyframes dome-rem {
  0%, 44% { transform: translateY(0) rotate(0deg); opacity:.7; }
  52% { transform: translateY(-3px) rotate(-8deg); opacity:1; }
  60% { transform: translateY(1px) rotate(5deg); opacity:.84; }
  68% { transform: translateY(-2px) rotate(-5deg); opacity:.96; }
  76%, 100% { transform: translateY(0) rotate(0deg); opacity:.7; }
}
@keyframes dome-zzz {
  0% { transform: translate(0,6px) rotate(-8deg); opacity:0; }
  18% { opacity:.9; }
  70% { opacity:.55; }
  100% { transform: translate(26px,-54px) rotate(12deg); opacity:0; }
}
@keyframes dome-trace { from { stroke-dashoffset: 326; } to { stroke-dashoffset: 0; } }
@keyframes dome-node { 0%,100% { opacity:.28; transform: scale(.8); } 46% { opacity:1; transform: scale(1.25); } }

/* A piscada não é mais um laço de CSS com período fixo: quem a dispara é o
   JS, em intervalos sorteados e com piscada dupla ocasional. Um metrônomo de
   5,2 s lê como máquina — é o olho que denuncia, não o desenho. O fechar é
   mais rápido que o abrir, como no olho de verdade. */
.dome-a-blink { transition: transform .16s cubic-bezier(.3,0,.2,1); }
.dome-root[data-piscar="1"] .dome-a-blink { transform: scaleY(.06); transition-duration: .07s; }
.dome-a-sleep { animation: dome-breathe 4.2s ease-in-out infinite; }
.dome-a-gaze { animation: dome-gaze 8.4s ease-in-out infinite; }
.dome-a-gaze-ponder { animation: dome-ponder-gaze 5.2s ease-in-out infinite; }
.dome-a-gaze-sad { animation: dome-sad-gaze 7.2s ease-in-out infinite; }
.dome-a-gaze-tilt { animation: dome-tilt 4.4s ease-in-out infinite; }
.dome-a-gaze-shrug { animation: dome-shrug 3.2s ease-in-out infinite; }
.dome-a-face-tilt { animation: dome-tilt 3.6s ease-in-out infinite; }
.dome-a-brow { animation: dome-glowy 3.4s ease-in-out infinite; }
.dome-a-brow-r { animation: dome-glowy 3.4s ease-in-out infinite; animation-delay: -.3s; }
.dome-a-brow-fret { animation: dome-fretty 1.9s ease-in-out infinite; }
.dome-a-brow-fret-r { animation: dome-fretty 1.9s ease-in-out infinite; animation-delay: -.3s; }
.dome-a-brow-rem { animation: dome-rem 6.4s ease-in-out infinite; }
.dome-a-brow-rem-r { animation: dome-rem 6.4s ease-in-out infinite; animation-delay: -3.2s; }
.dome-a-scan { animation: dome-scanline 1.4s linear infinite; }
.dome-a-dash { animation: dome-dash 1.05s ease-in-out infinite; }
.dome-a-dash-r { animation: dome-dash 1.05s ease-in-out infinite; animation-delay: -.24s; }
.dome-a-streak-0 { animation: dome-rush .72s ease-in-out infinite; }
.dome-a-streak-1 { animation: dome-rush .88s ease-in-out infinite; animation-delay: -.17s; }
.dome-a-streak-2 { animation: dome-rush 1.04s ease-in-out infinite; animation-delay: -.34s; }
.dome-a-halo-a { animation: dome-spin 1.9s linear infinite; }
.dome-a-halo-b { animation: dome-spin 4.2s linear infinite reverse; }
.dome-a-halo-pulse { animation: dome-shock 2.4s ease-out infinite; }
.dome-a-sweep { animation: dome-sweep 1.6s ease-in-out infinite; }
.dome-a-trace { animation: dome-trace 2.4s linear infinite; }
.dome-a-node-0 { animation: dome-node 2.4s ease-in-out infinite; }
.dome-a-node-1 { animation: dome-node 2.4s ease-in-out infinite; animation-delay: -.34s; }
.dome-a-node-2 { animation: dome-node 2.4s ease-in-out infinite; animation-delay: -.68s; }
.dome-a-node-3 { animation: dome-node 2.4s ease-in-out infinite; animation-delay: -1.02s; }
.dome-a-node-4 { animation: dome-node 2.4s ease-in-out infinite; animation-delay: -1.36s; }
.dome-a-boot-0 { animation: dome-boot 1.8s ease-out infinite; }
.dome-a-boot-1 { animation: dome-boot 1.8s ease-out infinite; animation-delay: -.2s; }
.dome-a-boot-2 { animation: dome-boot 1.8s ease-out infinite; animation-delay: -.4s; }
.dome-a-boot-3 { animation: dome-boot 1.8s ease-out infinite; animation-delay: -.6s; }
.dome-a-tear { animation: dome-tear 3.6s ease-in infinite; }
.dome-a-zzz-0 { animation: dome-zzz 4.5s ease-in-out infinite; }
.dome-a-zzz-1 { animation: dome-zzz 4.5s ease-in-out infinite; animation-delay: -1.5s; }
.dome-a-zzz-2 { animation: dome-zzz 4.5s ease-in-out infinite; animation-delay: -3s; }
.dome-a-mouth-0 { animation: dome-talk .48s ease-in-out infinite; }
.dome-a-mouth-1 { animation: dome-talk .53s ease-in-out infinite; animation-delay: -.09s; }
.dome-a-mouth-2 { animation: dome-talk .64s ease-in-out infinite; animation-delay: -.18s; }
.dome-a-mouth-3 { animation: dome-talk .42s ease-in-out infinite; animation-delay: -.27s; }
.dome-a-mouth-4 { animation: dome-talk .53s ease-in-out infinite; animation-delay: -.36s; }
.dome-a-mouth-5 { animation: dome-talk .64s ease-in-out infinite; animation-delay: -.45s; }
.dome-a-mouth-6 { animation: dome-talk .42s ease-in-out infinite; animation-delay: -.54s; }

/* a boca abre conforme o nível de áudio; a escala vive fora das barras para
   não brigar com o keyframe de fala, que já ocupa o transform de cada barra */
.dome-mouth {
  transform-box: fill-box;
  transform-origin: center;
  transform: scaleY(calc(.24 + var(--dome-level, 0) * .76));
}

/* Quem pediu menos movimento vê o mesmo rosto, parado na pose do estado:
   nenhuma expressão depende de animação para ser reconhecível. */
@media (prefers-reduced-motion: reduce) {
  .dome-root, .dome-root * { animation: none !important; }
  .dome-mouth { transform: scaleY(.7); }
  /* a piscada vem de atributo, não de animação — precisa ser desligada à parte */
  .dome-root[data-piscar="1"] .dome-a-blink { transform: none; }
  .dome-a-blink { transition: none; }
}
`;

let injected = false;

/** Injeta a folha uma única vez por documento. */
export function ensureDomeStyles(): void {
  if (injected || typeof document === 'undefined') return;
  injected = true;
  const el = document.createElement('style');
  el.setAttribute('data-will-dome', '');
  el.textContent = CSS;
  document.head.appendChild(el);
}
